<template>
  <div>
    <PageHeader v-if="!isMobile" :title="artwork.title" />
    <div v-if="artwork" class="max-w-6xl mx-auto my-0 px-4">
      <div :class="['max-w-2xl mx-auto text-center', isMobile ? 'mt-20' : 'mt-24']">
        <h1 class="text-center">{{ artwork.title }}</h1>
        <template v-if="artwork.related_people.length">
          <p v-for="(group, groupIndex) in artwork.related_people" :key="`group-${groupIndex}`">
            {{ capitalize(group.label) }}
            <span v-for="(person, personIndex) in group.people" :key="`person-${personIndex}`">
              <router-link :to="`/${person.uri.replace('people', 'about')}`">{{ person.title }}</router-link>
              <span v-if="personIndex < group.people.length - 1">, </span>
            </span>
          </p>
        </template>
        <p class="my-4">{{ displayDate }}</p>
        <Tags :tags="artwork.tags" class="my-4" />
        <div class="w-full relative my-16" :style="calcAspectRatioPadding(artwork.cover_image)">
          <img
            v-if="artwork.cover_image"
            class="absolute top-0 left-0 w-full h-full"
            :src="artwork.cover_image.src"
            :srcset="artwork.cover_image.srcset"
          />
        </div>
      </div>
      <ContentBlocks :blocks="artwork.description" />
      <RelatedItems :items="relatedItems" />
    </div>
    <div class="fixed left-0 bottom-0 p-4">
      {{ artwork.location }}
    </div>
    <PageFooter />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { capitalize, formatDateOrRange, generateMetaTags } from '@/util'
import PageHeader from '@/components/PageHeader'
import Tags from '@/components/Tags'
import ContentBlocks from '@/components/ContentBlocks'
import RelatedItems from '@/components/RelatedItems'
import PageFooter from '@/components/PageFooter'

export default {
  name: 'Artwork',

  metaInfo () {
    return {
      title: this.pageTitle,
      meta: generateMetaTags(this.pageTitle, this.pageDescription, this.pageImage)
    }
  },

  components: {
    PageHeader,
    Tags,
    ContentBlocks,
    RelatedItems,
    PageFooter
  },

  computed: {
    ...mapState(['artworks', 'isMobile']),

    pageTitle () {
      return this.artwork ? this.artwork.title : null
    },

    pageDescription () {
      return this.artwork ? this.artwork.description : null
    },

    pageImage () {
      return this.artwork && this.artwork.cover_image ? this.artwork.cover_image.src : null
    },

    slug () {
      return this.$route.params.slug
    },

    displayDate () {
      return formatDateOrRange(this.artwork.begin_date, this.artwork.end_date)
    },

    relatedItems () {
      const activities = this.artwork.related_activities.map(a => {
        a.type = 'Activity'
        a.date = a.begin_date
        a.dateDisplay = formatDateOrRange(a.begin_date, a.end_date)
        return a
      })

      const groups = this.artwork.related_activity_groups.map(g => {
        g.type = 'Activity Group'
        g.date = g.begin_date
        g.dateDisplay = formatDateOrRange(g.begin_date, g.end_date)
        return g
      })

      const events = this.artwork.related_events.map(e => {
        e.type = 'Event'
        e.date = e.begin_date
        e.dateDisplay = formatDateOrRange(e.begin_date, e.end_date)
        return e
      })

      const resources = this.artwork.related_resources.map(r => {
        r.type = 'Resource'
        r.date = r.publication_date
        r.dateDisplay = formatDateOrRange(r.publication_date)
        return r
      })

      return [...activities,...groups, ...events,  ...resources]
    }
  },

  methods: {
    calcAspectRatioPadding (image) {
      if (!image || !image.dimensions) {
        return ''
      }

      return `padding-top:calc(100%/(${image.dimensions.width}/${image.dimensions.height}));`
    },

    capitalize
  },

  data () {
    return {
      artwork: false
    }
  },

  async mounted () {
    await this.$store.dispatch('getArtwork', this.slug)
    this.artwork = this.artworks[this.slug]
    this.$Lazyload.lazyLoadHandler()
  }
}
</script>
